import React from 'react';
import ReactCountryFlag from 'react-country-flag';

// Patient language, as shown in the admin. `lang` is set from the site locale at
// signup by feeli-server and drives the patient's emails, API messages and the
// DeepL translation of the messenger thread — so support needs to see it before
// replying, and dispatch needs to be able to find these requests.
//
// Three states, not two, and the third is the whole point:
//
//   'fr'      the default locale, or a legacy account with no value — NOT badged,
//             since it is the vast majority and a flag on every row is noise
//   known     a locale this file has a label for — badged with its flag
//   unknown   a non-empty value this file does NOT recognise — badged with a
//             neutral marker
//
// Collapsing "unknown" into French is a silent, load-bearing mistake: `lang` is
// owned by feeli-server, so the moment it adds a locale (Hebrew being the obvious
// next one given the IL feature set), mapping it to French would show support an
// unbadged row for a patient who cannot read French. An unfamiliar value must be
// visible precisely BECAUSE this file does not know what it is.
const DEFAULT_LANG = 'fr';
export const UNKNOWN_LANG = 'unknown';

// The flag is the COUNTRY each locale is presented with across Feeli (the same
// pairing as feeli-client's LanguageSwitcher), not the language code — there is no
// such thing as a language flag, and GB/ES are what people already recognise.
const LANGS = {
    fr: { label: 'Français', short: 'FR', flag: 'FR' },
    en: { label: 'Anglais', short: 'EN', flag: 'GB' },
    es: { label: 'Espagnol', short: 'ES', flag: 'ES' },
};

// Options for the requests-list filter, translated locales first — those are the
// ones anyone actually goes looking for. Keep in sync with SUPPORTED_LANGS in
// server/Controllers/Utils/patientLang.js: an option this list offers but the
// server does not know now returns ZERO rows (fail-closed) rather than the whole
// unfiltered list, so a mismatch is visible rather than silently wrong.
export const LANG_FILTER_OPTIONS = [
    { value: 'es', label: '🇪🇸 Espagnol' },
    { value: 'en', label: '🇬🇧 Anglais' },
    { value: 'fr', label: '🇫🇷 Français' },
    { value: 'INT', label: 'International (non FR)'},
];

/**
 * Canonicalise a raw `lang` value.
 * - absent / empty  -> 'fr'          (legacy account: unknown, treated as French)
 * - a known locale  -> that locale
 * - anything else   -> UNKNOWN_LANG  (visible, never silently French)
 */
export const normalizeLang = (value) => {
    if (value === undefined || value === null) return DEFAULT_LANG;
    if (typeof value !== 'string') return UNKNOWN_LANG;
    const v = value.trim().toLowerCase();
    if (!v) return DEFAULT_LANG;
    return LANGS[v] ? v : UNKNOWN_LANG;
};

// The language of the patient behind a request. The list endpoint populates
// `userId.lang`; a request with no populated user has no language to report.
export const requestLang = (request) => (
    request && request.userId ? normalizeLang(request.userId.lang) : DEFAULT_LANG
);

/**
 * Marker for a patient who is not on the default locale. Renders nothing for
 * French, so rows stay clean and a badge always means "this one does not get a
 * French reply".
 *
 * Callers MUST be sure their endpoint actually selects `lang` — an endpoint that
 * omits it hands us `undefined`, which is indistinguishable from a legacy French
 * account and renders no badge. See the populate in FeedbackController /
 * RequestsController.
 */
export function LangFlag({ lang, size = '1.2em', prefix = '' }) {
    const code = normalizeLang(lang);
    if (code === DEFAULT_LANG) return null;

    if (code === UNKNOWN_LANG) {
        // Deliberately loud rather than absent: we know the patient is not on the
        // default locale, we just cannot say which one it is.
        return (
            <span
                title={`${prefix}langue inconnue (${String(lang)}) — vérifier avant de répondre`}
                style={{ marginLeft: 6, whiteSpace: 'nowrap', fontWeight: 700, color: '#b8860b' }}
            >
                ?
            </span>
        );
    }

    const { label, short, flag } = LANGS[code];
    return (
        <span title={`${prefix}${label}`} style={{ marginLeft: 6, whiteSpace: 'nowrap' }}>
            <ReactCountryFlag
                countryCode={flag}
                svg
                title={short}
                style={{ fontSize: size, lineHeight: 1 }}
            />
        </span>
    );
}
